import { useEffect, useState } from 'react';
import { Box, Card, Link, Title } from '@nimbus-ds/components';
import { Layout, Page } from '@nimbus-ds/patterns';
import { Prompt } from 'react-router';
import { OrdersAutoCancelPaymentStatusConfiguration } from '@tiendanube/common/src/domains/orders/orders';
import {
  OrdersAutoCancelPaymentMethod,
  OrdersAutoCancelPaymentStatus,
} from '@tiendanube/common/src/enums';
import useNavegate from 'App/hooks/useNavegate';
import {
  CancelAndConfirmButtons,
  HeaderTop,
  IonPageStratus,
  useResponsive,
} from 'commons/components';
import goToAdmin from 'commons/utils/gotToAdmin';
import { trackingOrdersAutoCancelPixConfigurationButtonClick } from 'domains/Orders/tracking';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import {
  useGetNuvemPagoFromPorviderList,
  useInstalledPaymentProviders,
} from 'domains/Payments/PaymentProviders/hooks';
import Options from './components/Options/Options';
import PaymentStatusConfig from './components/PaymentStatusConfig/PaymentStatusConfig';
import useOrdersAutoCancelConfiguration from './hooks/useOrdersAutoCancelConfiguration';
import OrdersAutoCancelPageError from './OrdersAutoCancelPageError';
import OrdersAutoCancelPageHeader from './OrdersAutoCancelPageHeader';
import OrdersAutoCancelPageSkeleton from './OrdersAutoCancelPageSkeleton';
import './OrdersAutoCancelPage.scss';

function OrdersAutoCancelPage(): JSX.Element {
  const { goBack } = useNavegate();
  const {
    fetchedConfiguration,
    configuration,
    setConfiguration,
    hasConfigChanged,
    isLoading,
    isError,
    isSaving,
    onSubmit,
    getConfiguration,
    validateConfiguration,
  } = useOrdersAutoCancelConfiguration();
  const t = useTranslationOrders();
  const { paymentProviders, fetchInstalledPaymentProviders } =
    useInstalledPaymentProviders();
  const { nuvemPagoIsActive } =
    useGetNuvemPagoFromPorviderList(paymentProviders);
  const { isMobile } = useResponsive();

  const [canSave, setCanSave] = useState<boolean>(
    configuration?.isNew === false && !!nuvemPagoIsActive,
  );

  const [isDirty, setIsDirty] = useState<boolean>(false);

  const [paymentMethodsWithError, setPaymentMethodsWithError] = useState<
    | Partial<
        Record<OrdersAutoCancelPaymentStatus, OrdersAutoCancelPaymentMethod[]>
      >
    | []
  >([]);

  useEffect(() => {
    if (!paymentProviders) {
      fetchInstalledPaymentProviders();
    }
  }, [paymentProviders, fetchInstalledPaymentProviders]);

  useEffect(() => {
    setConfiguration(fetchedConfiguration);
    setCanSave(fetchedConfiguration?.isNew === false && !!nuvemPagoIsActive);
  }, [fetchedConfiguration, nuvemPagoIsActive, setConfiguration]);

  useEffect(() => {
    setIsDirty(hasConfigChanged);
  }, [hasConfigChanged]);

  if (isLoading) {
    return <OrdersAutoCancelPageSkeleton />;
  }

  if (isError || !configuration) {
    return <OrdersAutoCancelPageError onRetry={getConfiguration} />;
  }

  const onPaymentStatusConfigChange = (
    paymentStatus: OrdersAutoCancelPaymentStatus,
    paymentConfig: OrdersAutoCancelPaymentStatusConfiguration,
  ) => {
    const newConfig = {
      ...configuration,
      configuration: {
        ...configuration.configuration,
        [paymentStatus]: paymentConfig,
      },
    };

    if (configuration.isNew) {
      const anyMethodIsActive = Object.values(newConfig.configuration).find(
        (methods) =>
          Object.values(methods).find(
            (config) => config.isActive && !!config.expirationTime,
          ),
      );
      setCanSave(!!anyMethodIsActive);
    }
    setPaymentMethodsWithError([]);
    setConfiguration(newConfig);
  };

  const onUpdateStockChange = (value: boolean) => {
    setConfiguration({
      ...configuration,
      updateStock: value,
    });
  };

  const onNotifyClientChange = (value: boolean) => {
    setConfiguration({
      ...configuration,
      notifyClient: value,
    });
  };

  const onConfigurationSave = () => {
    const emptyConfigurations = validateConfiguration(configuration);
    setPaymentMethodsWithError(emptyConfigurations);
    const isValidConfig = !Object.values(emptyConfigurations).find(
      (methods) => methods.length > 0,
    );
    if (isValidConfig) {
      onSubmit(configuration);
    }
  };

  const onPixConfigurationClick = () => {
    trackingOrdersAutoCancelPixConfigurationButtonClick();
    goToAdmin('/nuvempago/settings')();
  };

  return (
    <div className="stratus--orders-autocancel-page">
      <IonPageStratus
        headerTop={<HeaderTop navigation={{ onClick: goBack }} />}
        headerContent={
          <OrdersAutoCancelPageHeader
            hasPreviousConfig={!configuration.isNew}
            isNuvemPagoActive={!!nuvemPagoIsActive}
          />
        }
        width="small"
      >
        <Prompt when={isDirty} message={t('common:exitEdit.info')} />
        <div
          className={isMobile ? 'stratus--orders-autocancel-page-mobile' : ''}
        >
          <Page.Body>
            <Layout columns="1">
              <Layout.Section>
                {nuvemPagoIsActive && (
                  <Card>
                    <Card.Header>
                      <Box
                        alignItems="center"
                        display="flex"
                        justifyContent="space-between"
                      >
                        <Title as="h4">
                          {t(`autoCancel.paymentStatus.pending`)}
                        </Title>
                        <Link
                          as="a"
                          textDecoration="none"
                          appearance="primary"
                          onClick={onPixConfigurationClick}
                        >
                          {t('autoCancel.pendingNuvempagoLink')}
                        </Link>
                      </Box>
                    </Card.Header>
                  </Card>
                )}
                {Object.entries(configuration.configuration).map(
                  ([statusValue, methods]) => (
                    <Box key={`box-${statusValue}`}>
                      <PaymentStatusConfig
                        paymentStatus={
                          statusValue as OrdersAutoCancelPaymentStatus
                        }
                        enabledPaymentMethods={
                          Object.keys(
                            methods,
                          ) as OrdersAutoCancelPaymentMethod[]
                        }
                        configuration={
                          configuration?.configuration[statusValue]
                        }
                        onChange={onPaymentStatusConfigChange}
                        disabled={!nuvemPagoIsActive}
                        paymentMethodsWithError={
                          paymentMethodsWithError[statusValue] ?? []
                        }
                      />
                    </Box>
                  ),
                )}
                <Options
                  updateStock={configuration.updateStock}
                  notifyClient={configuration.notifyClient}
                  onUpdateStockChange={onUpdateStockChange}
                  onNotifyClientChange={onNotifyClientChange}
                  disabled={!canSave}
                />
                <Box justifyContent="flex-end">
                  <CancelAndConfirmButtons
                    isConfirmDisabled={!canSave || !isDirty}
                    onCancel={() => goBack()}
                    onConfirm={onConfigurationSave}
                    isLoading={isSaving}
                    confirmText={t('autoCancel.save')}
                  />
                </Box>
              </Layout.Section>
            </Layout>
          </Page.Body>
        </div>
      </IonPageStratus>
    </div>
  );
}

export default OrdersAutoCancelPage;
